import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../utils/auth";
import {
  fetchBulkWhatsAppTemplates,
  addBulkWhatsAppTemplate,
  deleteBulkWhatsAppTemplate,
  sendBulkWhatsAppTemplate,
} from "../../api/OrderAPIs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MessageSquare, Send, ArrowRight, Plus, Trash2 } from "lucide-react";

const BulkWhatsAppPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [phoneNumbers, setPhoneNumbers] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const [newTemplateId, setNewTemplateId] = useState("");
  const [newTemplateName, setNewTemplateName] = useState("");
  const [newTemplateDescription, setNewTemplateDescription] = useState("");
  const [addingTemplate, setAddingTemplate] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const data = await fetchBulkWhatsAppTemplates();
      setTemplates(data);
      if (data.length > 0) {
        setSelectedTemplateId((prev) => {
          const stillExists = data.some((t) => String(t.templateId) === prev);
          return stillExists ? prev : String(data[0].templateId);
        });
      } else {
        setSelectedTemplateId("");
      }
    } catch (err) {
      toast.error(err.message || "فشل تحميل القوالب");
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    if (authState.role === "Admin" || authState.role === "Moderator") {
      loadTemplates();
    } else {
      setLoadingTemplates(false);
    }
  }, [authState.role, loadTemplates]);

  const selectedTemplate = templates.find((t) => String(t.templateId) === selectedTemplateId);

  const handleAddTemplate = async (e) => {
    e.preventDefault();

    const templateId = Number(newTemplateId);
    if (!templateId || templateId <= 0) {
      toast.error("أدخل رقم قالب صحيح من BeOn");
      return;
    }
    if (!newTemplateName.trim()) {
      toast.error("أدخل اسم القالب");
      return;
    }

    setAddingTemplate(true);
    try {
      await addBulkWhatsAppTemplate({
        templateId,
        name: newTemplateName.trim(),
        description: newTemplateDescription.trim() || null,
      });
      toast.success("تم إضافة القالب");
      setNewTemplateId("");
      setNewTemplateName("");
      setNewTemplateDescription("");
      setShowAddForm(false);
      await loadTemplates();
      setSelectedTemplateId(String(templateId));
    } catch (err) {
      toast.error(err.message || "فشل إضافة القالب");
    } finally {
      setAddingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (recordId, name) => {
    if (!window.confirm(`حذف القالب "${name}"؟`)) return;

    try {
      await deleteBulkWhatsAppTemplate(recordId);
      toast.success("تم حذف القالب");
      await loadTemplates();
    } catch (err) {
      toast.error(err.message || "فشل حذف القالب");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedTemplateId) {
      toast.error("اختر قالباً أو أضف قالباً جديداً");
      return;
    }

    if (!phoneNumbers.trim()) {
      toast.error("أدخل رقم واحد على الأقل");
      return;
    }

    setLoading(true);
    setResults(null);

    try {
      const data = await sendBulkWhatsAppTemplate(Number(selectedTemplateId), phoneNumbers);
      setResults(data);
      toast.success(`تم الإرسال لـ ${data.processed} من ${data.total} رقم`);
    } catch (err) {
      toast.error(err.message || "فشل الإرسال");
    } finally {
      setLoading(false);
    }
  };

  if (authState.role !== "Admin" && authState.role !== "Moderator") {
    return (
      <div className="min-h-screen flex items-center justify-center text-white bg-gray-900">
        غير مصرح
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-emerald-900 to-gray-900 text-white p-6" dir="rtl">
      <ToastContainer position="top-center" theme="dark" />
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate("/dashboard")}
          className="mb-6 flex items-center gap-2 text-emerald-200 hover:text-white"
        >
          <ArrowRight size={18} />
          العودة للوحة التحكم
        </button>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6 shadow-xl mb-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <MessageSquare className="text-emerald-300" />
              <h1 className="text-2xl font-bold">إرسال قوالب WhatsApp</h1>
            </div>
            <button
              type="button"
              onClick={() => setShowAddForm((v) => !v)}
              className="flex items-center gap-2 bg-emerald-700/60 hover:bg-emerald-600/80 px-4 py-2 rounded-xl text-sm font-medium transition"
            >
              <Plus size={16} />
              {showAddForm ? "إلغاء" : "إضافة قالب"}
            </button>
          </div>

          {showAddForm && (
            <form onSubmit={handleAddTemplate} className="mb-6 p-4 bg-black/20 rounded-xl border border-white/10 space-y-3">
              <p className="text-sm text-emerald-100 font-medium">قالب جديد من BeOn</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="number"
                  value={newTemplateId}
                  onChange={(e) => setNewTemplateId(e.target.value)}
                  placeholder="رقم القالب (مثل 83267)"
                  className="rounded-xl bg-black/30 border border-white/20 p-3 text-white placeholder-emerald-200/50 focus:outline-none focus:ring-2 focus:ring-emerald-400 font-mono text-left"
                  dir="ltr"
                />
                <input
                  type="text"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  placeholder="اسم يظهر في القائمة"
                  className="rounded-xl bg-black/30 border border-white/20 p-3 text-white placeholder-emerald-200/50 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
              </div>
              <input
                type="text"
                value={newTemplateDescription}
                onChange={(e) => setNewTemplateDescription(e.target.value)}
                placeholder="وصف اختياري"
                className="w-full rounded-xl bg-black/30 border border-white/20 p-3 text-white placeholder-emerald-200/50 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
              <button
                type="submit"
                disabled={addingTemplate}
                className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 rounded-xl py-2.5 font-semibold transition"
              >
                {addingTemplate ? "جاري الحفظ..." : "حفظ القالب"}
              </button>
            </form>
          )}

          {loadingTemplates ? (
            <p className="text-emerald-200 text-sm">جاري تحميل القوالب...</p>
          ) : templates.length === 0 ? (
            <p className="text-amber-200 text-sm">
              لا توجد قوالب بعد. اضغط «إضافة قالب» وأدخل رقم القالب من BeOn.
            </p>
          ) : (
            <ul className="space-y-2 mb-6">
              {templates.map((t) => (
                <li
                  key={t.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-xl bg-black/20 border border-white/10"
                >
                  <div>
                    <p className="font-medium">{t.name}</p>
                    <p className="text-xs text-emerald-200/70 font-mono" dir="ltr">
                      BeOn ID: {t.templateId}
                    </p>
                    {t.description && (
                      <p className="text-xs text-emerald-200/60 mt-1">{t.description}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteTemplate(t.id, t.name)}
                    className="p-2 text-red-300 hover:text-red-200 hover:bg-red-900/30 rounded-lg transition shrink-0"
                    title="حذف"
                  >
                    <Trash2 size={18} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <p className="text-emerald-100 mb-4 text-sm leading-relaxed">
            أدخل أرقام العملاء (رقم في كل سطر)، واختر القالب المراد إرساله. لا يتم تعديل حالة الطلب.
          </p>

          {templates.length > 0 && (
            <form onSubmit={handleSubmit}>
              <label className="block text-sm font-medium text-emerald-100 mb-2">القالب للإرسال</label>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="w-full rounded-xl bg-black/30 border border-white/20 p-3 text-white focus:outline-none focus:ring-2 focus:ring-emerald-400 mb-4"
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.templateId} className="bg-gray-900">
                    {t.name} (ID: {t.templateId})
                  </option>
                ))}
              </select>

              {selectedTemplate?.description && (
                <p className="text-emerald-200/80 text-sm mb-4">{selectedTemplate.description}</p>
              )}

              <label className="block text-sm font-medium text-emerald-100 mb-2">أرقام الموبايل</label>
              <textarea
                value={phoneNumbers}
                onChange={(e) => setPhoneNumbers(e.target.value)}
                rows={10}
                placeholder={"01002154996\n01555069856\n01234567890"}
                className="w-full rounded-xl bg-black/30 border border-white/20 p-4 text-white placeholder-emerald-200/50 focus:outline-none focus:ring-2 focus:ring-emerald-400 font-mono text-left"
                dir="ltr"
              />

              <button
                type="submit"
                disabled={loading}
                className="mt-4 w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 rounded-xl py-3 font-semibold transition"
              >
                <Send size={18} />
                {loading ? "جاري الإرسال..." : "إرسال القالب"}
              </button>
            </form>
          )}

          {results && (
            <div className="mt-6 bg-black/20 rounded-xl p-4 border border-white/10">
              <p className="mb-3 font-semibold">
                {results.templateName} (#{results.templateId}) — {results.processed} ناجح من {results.total}
              </p>
              <ul className="space-y-2 text-sm max-h-64 overflow-y-auto">
                {results.results?.map((r, i) => (
                  <li key={i} className={`p-2 rounded ${r.success ? "bg-green-900/30" : "bg-red-900/30"}`}>
                    <span className="font-mono">{r.phone}</span>
                    {r.error && <span className="text-red-300"> — {r.error}</span>}
                    {r.success && <span className="text-green-300"> — تم الإرسال</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkWhatsAppPage;
